import AsyncStorage from "@react-native-async-storage/async-storage";

import type { Href } from "expo-router";

import { logAdminAction } from "./auditLog";
import { supabase } from "./supabase";

export type InviteKind = "client" | "judge" | "employee" | "manager";
export type InviteStatus = "pending" | "accepted" | "revoked" | "expired";

export type InviteRow = {
  id: string;
  kind: InviteKind;
  email: string;
  token: string;
  company_id: string | null;
  competition_id: string | null;
  role: string | null;
  judge_id: string | null;
  permissions: string[] | null;
  invited_by: string;
  status: InviteStatus;
  accepted_user_id: string | null;
  accepted_at: string | null;
  expires_at: string;
  created_at: string;
};

export type InviteByToken = {
  id: string;
  kind: InviteKind;
  email: string;
  status: InviteStatus;
  expires_at: string;
  company_id: string | null;
  competition_id: string | null;
  role: string | null;
  permissions: string[] | null;
};

export type AcceptInviteResult = {
  kind: InviteKind;
  role: string | null;
  company_id: string | null;
  competition_id: string | null;
};

/** Persisted when email confirmation is required before the user can accept. */
export const PENDING_INVITE_TOKEN_KEY = "coravu_pending_invite_token";

export async function setPendingInviteToken(token: string | null): Promise<void> {
  if (token) await AsyncStorage.setItem(PENDING_INVITE_TOKEN_KEY, token);
  else await AsyncStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
}

export async function getPendingInviteToken(): Promise<string | null> {
  return AsyncStorage.getItem(PENDING_INVITE_TOKEN_KEY);
}

export async function clearPendingInviteToken(): Promise<void> {
  await AsyncStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
}

export async function listInvites(filter?: { kind?: InviteKind; status?: InviteStatus }): Promise<InviteRow[]> {
  let q = supabase.from("invites").select("*").order("created_at", { ascending: false });
  if (filter?.kind) q = q.eq("kind", filter.kind);
  if (filter?.status) q = q.eq("status", filter.status);
  const { data, error } = await q;
  if (error || !data) return [];
  return data as InviteRow[];
}

export async function createInvite(input: {
  kind: InviteKind;
  email: string;
  role?: string;
  companyId?: string;
  competitionId?: string;
  judgeId?: string;
  permissions?: string[];
}): Promise<InviteRow> {
  // TODO: when Resend is configured, also call an Edge Function to email the invite link to the invitee.
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error("Not signed in");
  const { data, error } = await supabase
    .from("invites")
    .insert({
      kind: input.kind,
      email: input.email.trim().toLowerCase(),
      role: input.role ?? null,
      company_id: input.companyId ?? null,
      competition_id: input.competitionId ?? null,
      judge_id: input.judgeId ?? null,
      permissions: input.permissions ?? null,
      invited_by: userData.user.id,
    })
    .select("*")
    .single();
  if (error || !data) throw new Error(error?.message ?? "Failed to create invite");
  const row = data as InviteRow;
  // Audit only fires for admin callers; for invites created by clients
  // (e.g. inviting teammates), the SECURITY DEFINER `log_admin_action`
  // refuses with an admin-required error and the lib's swallow-and-warn
  // path keeps the user flow uninterrupted.
  await logAdminAction({
    action: "invite.create",
    entityType: "invites",
    entityId: row.id,
    summary: `Created ${row.kind} invite for ${row.email}`,
    diff: { after: { kind: row.kind, email: row.email, company_id: row.company_id, competition_id: row.competition_id } },
  });
  return row;
}

export async function revokeInvite(id: string): Promise<void> {
  const { error } = await supabase.from("invites").update({ status: "revoked" }).eq("id", id);
  if (error) throw new Error(error.message);
  await logAdminAction({
    action: "invite.revoke",
    entityType: "invites",
    entityId: id,
    summary: `Revoked invite ${id.slice(0, 8)}…`,
  });
}

function unwrapRpcRow<T>(data: unknown): T | null {
  if (data == null) return null;
  if (Array.isArray(data)) return (data[0] as T) ?? null;
  return data as T;
}

export async function getInviteByToken(token: string): Promise<InviteByToken | null> {
  const trimmed = token.trim();
  if (!trimmed) return null;
  const { data, error } = await supabase.rpc("get_invite_by_token", { p_token: trimmed });
  if (error) return null;
  const row = unwrapRpcRow<InviteByToken>(data);
  if (!row || typeof row !== "object") return null;
  return row;
}

export async function acceptInvite(token: string): Promise<AcceptInviteResult> {
  const trimmed = token.trim();
  if (!trimmed) throw new Error("Invalid token");
  const { data, error } = await supabase.rpc("accept_invite", { p_token: trimmed });
  if (error) throw new Error(error.message);
  const row = unwrapRpcRow<Record<string, unknown>>(data);
  if (!row) throw new Error("Failed to accept invite");
  return {
    kind: row.kind as InviteKind,
    role: (row.role as string) ?? null,
    company_id: (row.company_id as string) ?? null,
    competition_id: (row.competition_id as string) ?? null,
  };
}

const DEFAULT_SITE = "https://coravu.com";

/** Build the URL the invitee will click. Stored in clipboard / sent via email. */
export function buildInviteUrl(token: string): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/accept-invite?token=${encodeURIComponent(token)}`;
  }
  const base = (process.env.EXPO_PUBLIC_SITE_URL ?? DEFAULT_SITE).replace(/\/$/, "");
  return `${base}/accept-invite?token=${encodeURIComponent(token)}`;
}

export function portalHrefAfterAccept(result: AcceptInviteResult): Href {
  switch (result.kind) {
    case "client":
      return "/client" as Href;
    case "judge":
      return "/judge" as Href;
    case "employee":
      return "/onboard/employee" as Href;
    default:
      return "/" as Href;
  }
}

/** Best-effort labels for the public accept page (anon may not read company/competition rows). */
export async function fetchInviteDisplayNames(invite: InviteByToken): Promise<{
  companyName: string | null;
  competitionName: string | null;
}> {
  let companyName: string | null = null;
  let competitionName: string | null = null;
  if (invite.company_id) {
    const { data } = await supabase.from("companies").select("name").eq("id", invite.company_id).maybeSingle();
    if (data && typeof (data as { name?: string }).name === "string") {
      companyName = (data as { name: string }).name;
    }
  }
  if (invite.competition_id) {
    const { data } = await supabase.from("competitions").select("name").eq("id", invite.competition_id).maybeSingle();
    if (data && typeof (data as { name?: string }).name === "string") {
      competitionName = (data as { name: string }).name;
    }
  }
  return { companyName, competitionName };
}

export function isEmailMismatchAcceptError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes("email") &&
    (m.includes("match") || m.includes("does not") || m.includes("mismatch") || m.includes("wrong"))
  );
}
